import { Controller, useForm } from 'react-hook-form';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { DatePicker, Select } from 'antd';
import { t } from 'i18next';
import { useAppDispatch, useAppSelector } from 'core/store/hooks';
import LinksButton from 'shared/design-system/components/links-button';
import { filtersDashboardSalUsedSelector, filtersDashboardProjectSelector } from '../_redux/selectors';
import { saveDashboardSalFilters } from '../_redux/actions';
import dayjs from 'dayjs';
import { mappedTipologiaBloccoEnumList } from 'enums/billingOptions';
import { filterOption } from 'shared/utils/common.utils';
import { StatusBadgeData } from 'enums/status';

export const filtersDashboardSalData: any = {
  pkTipologiaBlocco: undefined,
  dataInvioSalStart: undefined,
  dateInvioSalEnd: undefined,
  plUserId: undefined,
  plV2UserId: undefined,
  idCliente: undefined,
  idStrutturaBusiness: undefined,
};

interface Props {
  onSubmit: any;
  handleReset: () => void;
}
const FiltersDashboardSal = ({ onSubmit, handleReset }: Props) => {
  const filterUsed = useAppSelector(filtersDashboardSalUsedSelector);
  const filtri = useAppSelector(filtersDashboardProjectSelector);
  const dispatch = useAppDispatch();
  const { control, register, handleSubmit, reset } = useForm({
    defaultValues: filtersDashboardSalData,
    values: filterUsed,
  });

  const Reset = () => {
    reset();
    handleReset();
  };

  const handleFormSubmit = async (formData: any) => {
    dispatch(saveDashboardSalFilters(formData));
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  return (
    <Form className="grid" onSubmit={handleSubmit(handleFormSubmit)}>
      <Row>
        <Col sm="3">
          <Form.Label>{t('dashboard.projects.filters.project-lead')}</Form.Label>
          <Controller
            name="plUserId"
            control={control}
            render={({ field }) => (
              <Select
                showSearch
                size="large"
                className="w-100"
                allowClear
                optionFilterProp="children"
                filterOption={filterOption}
                value={field.value}
                onChange={(value) => field.onChange(value)}
                onBlur={field.onBlur}
                placeholder={t('dashboard.projects.filters.search-pl-placeholder')}
              >
                {filtri?.projectLeads?.map((el, index) => (
                  <Select.Option key={index} value={el.id}>
                    {el.nominativo}
                  </Select.Option>
                ))}
              </Select>
            )}
          />
        </Col>
        <Col sm="3">
          <Form.Label>{t('dashboard.projects.filters.project-lead-lv-2')}</Form.Label>
          <Controller
            name="plV2UserId"
            control={control}
            render={({ field }) => (
              <Select
                showSearch
                size="large"
                className="w-100"
                allowClear
                optionFilterProp="children"
                filterOption={filterOption}
                value={field.value}
                onChange={(value) => field.onChange(value)}
                onBlur={field.onBlur}
                placeholder={t('dashboard.projects.filters.search-pl-lv-2-placeholder')}
              >
                {filtri?.projectLeads?.map((el, index) => (
                  <Select.Option key={index} value={el.id}>
                    {el.nominativo}
                  </Select.Option>
                ))}
              </Select>
            )}
          />
        </Col>
        <Col sm="3">
          <Form.Label>{t('dashboard.projects.filters.account')}</Form.Label>
          <Controller
            name="idCliente"
            control={control}
            render={({ field }) => (
              <Select
                showSearch
                size="large"
                className="w-100"
                allowClear
                optionFilterProp="children"
                filterOption={filterOption}
                value={field.value}
                onChange={(value) => field.onChange(value)}
                onBlur={field.onBlur}
                placeholder={t('dashboard.projects.filters.search-account-placeholder')}
              >
                {filtri?.clientIds?.map((el, index) => (
                  <Select.Option key={index} value={el.id}>
                    {el.denominazione}
                  </Select.Option>
                ))}
              </Select>
            )}
          />
        </Col>
        <Col sm="3">
          <Form.Label>{t('Block type')}</Form.Label>
          <Controller
            name="pkTipologiaBlocco"
            control={control}
            render={({ field }) => (
              <Select
                showSearch
                size="large"
                className="w-100"
                allowClear
                value={field.value}
                onChange={(value) => field.onChange(value)}
                onBlur={field.onBlur}
                placeholder={t('Select block type')}
              >
                {mappedTipologiaBloccoEnumList?.map((el, index) => (
                  <Select.Option key={index} value={el.id}>
                    {el.label}
                  </Select.Option>
                ))}
              </Select>
            )}
          />
        </Col>
      </Row>
      <Row className="mt-3">
        <Col sm="3">
          <Form.Label>{t('dashboard.projects.filters.business-unit')}</Form.Label>
          <Controller
            name="idStrutturaBusiness"
            control={control}
            render={({ field }) => (
              <Select
                showSearch
                size="large"
                className="w-100"
                allowClear
                value={field.value}
                onChange={(value) => field.onChange(value)}
                onBlur={field.onBlur}
                placeholder={t('cdg.business-unit-placeholder')}
              >
                {filtri?.businessUnit?.map((el, index) => (
                  <Select.Option key={index} value={el.id}>
                    {el.name}
                  </Select.Option>
                ))}
              </Select>
            )}
          />
        </Col>
        <Col sm="3">
          <Form.Label>{t('Start date')}</Form.Label>
          <Controller
            name="dataInvioSalStart"
            control={control}
            defaultValue={undefined}
            render={({ field }) => (
              <DatePicker
                allowClear
                placeholder={`${t('project-list.start-date-placeholder')}`}
                className="w-100"
                {...field}
                ref={field.ref}
                name={field.name}
                onBlur={field.onBlur}
                format="DD/MM/YYYY"
                value={field.value ? dayjs(field.value) : undefined}
                size="large"
                onChange={(date: any) => {
                  field.onChange(date ? date.format('YYYY-MM-DD') : null);
                }}
              />
            )}
          />
        </Col>
        <Col sm="3">
          <Form.Label>{t('End date')}</Form.Label>
          <Controller
            name="dateInvioSalEnd"
            control={control}
            defaultValue={undefined}
            render={({ field }) => (
              <DatePicker
                allowClear
                placeholder={`${t('project-list.end-date-placeholder')}`}
                className="w-100"
                {...field}
                ref={field.ref}
                name={field.name}
                onBlur={field.onBlur}
                format="DD/MM/YYYY"
                value={field.value ? dayjs(field.value) : undefined}
                size="large"
                onChange={(date: any) => {
                  field.onChange(date ? date.format('YYYY-MM-DD') : undefined);
                }}
              />
            )}
          />
        </Col>
      </Row>

      <div className="d-flex mt-3">
        <div className="d-flex ms-auto">
          <Button className="btn text-danger trash-btn" type="button" variant="icon" onClick={Reset}>
            <i className="fa-regular fa-trash-can me-2" />
            {t('project-list.delete-filters')}
          </Button>
          <LinksButton type="submit" className="btn-sm btn-primary btn-links px-4">
            <i className="fa-solid fa-magnifying-glass me-2" />
            {t('project-list.search')}
          </LinksButton>
        </div>
      </div>
    </Form>
  );
};
export default FiltersDashboardSal;
